import { useState, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    Divider,
    Autocomplete,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { IdentifyResult, TaxReturnFormType, TaxReturnFormTypeLabels } from "../../interface/returns";

interface IdentifyPageSelectorParams {
    currentPageNumber: number;
    totalPages: number;
    identificationData: IdentifyResult[];
    onUpdate: (updatedData: IdentifyResult[]) => void;
    onSave: () => void;
    hasChanges: boolean;
    isSaving: boolean;
}

const IdentifyPageSelector = ({
    currentPageNumber,
    totalPages,
    identificationData,
    onUpdate,
    onSave,
    hasChanges,
    isSaving,
}: IdentifyPageSelectorParams) => {
    // Find the identification entry for the current page
    const currentPageData = useMemo(() => identificationData.find((entry) => entry.pageNumber === currentPageNumber), [identificationData, currentPageNumber]);

    // Local state for form fields
    const [formType, setFormType] = useState<TaxReturnFormType | "">("");
    const [formPage, setFormPage] = useState<number>(1);
    const [taxYear, setTaxYear] = useState<number>(new Date().getFullYear());

    // Refs for tab navigation
    const formTypeRef = useRef<HTMLInputElement>(null);
    const formPageRef = useRef<HTMLInputElement>(null);
    const taxYearRef = useRef<HTMLInputElement>(null);

    // Form type options for Autocomplete
    const formTypeOptions = useMemo(() => Object.keys(TaxReturnFormTypeLabels).map((key) => ({
            label: TaxReturnFormTypeLabels[key as TaxReturnFormType],
            value: key,
        })), []);

    // Update local state when current page changes
    useEffect(() => {
        if (currentPageData?.form) {
            setFormType(currentPageData.form.type as TaxReturnFormType);
            setFormPage(currentPageData.form.page);
        } else {
            setFormType("");
            setFormPage(1);
        }
        setTaxYear(currentPageData?.taxYear ?? new Date().getFullYear());
    }, [currentPageData]);

    // Keyboard shortcuts for focusing inputs
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Only handle if not already in an input
            if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
                return;
            }

            if (event.key === 'f' || event.key === 'F') {
                event.preventDefault();
                formTypeRef.current?.focus();
            } else if (event.key === 'p' || event.key === 'P') {
                event.preventDefault();
                formPageRef.current?.focus();
            } else if (event.key === 'y' || event.key === 'Y') {
                event.preventDefault();
                taxYearRef.current?.focus();
            } else if (event.key === 'x' || event.key === 'X') {
                event.preventDefault();
                handleFormTypeChange("");
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleFormTypeChange = (value: TaxReturnFormType | "") => {
        setFormType(value);
        updatePageData(value, formPage, taxYear);
    };

    const handleFormPageChange = (value: string) => {
        // Allow empty string or valid numbers
        if (value === "") {
            setFormPage(0);
            return;
        }
        const numValue = Number(value);
        if (!Number.isNaN(numValue)) {
            setFormPage(numValue);
            updatePageData(formType, numValue, taxYear);
        }
    };

    const handleTaxYearChange = (value: string) => {
        // Allow empty string or valid numbers
        if (value === "") {
            setTaxYear(0);
            return;
        }
        const numValue = Number(value);
        if (!Number.isNaN(numValue)) {
            setTaxYear(numValue);
            updatePageData(formType, formPage, numValue);
        }
    };

    const updatePageData = (type: TaxReturnFormType | "", page: number, year: number) => {
        // Don't update if page or year is 0 (empty)
        if (page === 0 || year === 0) {
            return;
        }

        const updatedData = [...identificationData];
        const existingIndex = updatedData.findIndex((entry) => entry.pageNumber === currentPageNumber);

        const newEntry: IdentifyResult = {
            pageNumber: currentPageNumber,
            isOnList: type !== "",
            isStateTaxReturn: currentPageData?.isStateTaxReturn ?? false,
            identifiedForm: currentPageData?.identifiedForm,
            confidence: currentPageData?.confidence ?? 0,
            reasoning: currentPageData?.reasoning ?? "",
            taxYear: year,
            form: type !== "" ? {
                type: String(type),
                page,
            } : undefined,
        };

        if (existingIndex >= 0) {
            updatedData[existingIndex] = newEntry;
        } else {
            // Insert in the correct position based on pageNumber
            const insertIndex = updatedData.findIndex((entry) => entry.pageNumber > currentPageNumber);
            if (insertIndex >= 0) {
                updatedData.splice(insertIndex, 0, newEntry);
            } else {
                updatedData.push(newEntry);
            }
        }

        onUpdate(updatedData);
    };

    const handleAddPage = () => {
        if (identificationData.some((entry) => entry.pageNumber === currentPageNumber)) {
            return; // Page already exists
        }

        const newEntry: IdentifyResult = {
            pageNumber: currentPageNumber,
            isOnList: false,
            isStateTaxReturn: false,
            taxYear: new Date().getFullYear(),
            confidence: 0,
            reasoning: "",
        };

        const updatedData = [...identificationData];
        const insertIndex = updatedData.findIndex((entry) => entry.pageNumber > currentPageNumber);
        if (insertIndex >= 0) {
            updatedData.splice(insertIndex, 0, newEntry);
        } else {
            updatedData.push(newEntry);
        }

        onUpdate(updatedData);
    };

    const handleRemovePage = () => {
        const updatedData = identificationData.filter((entry) => entry.pageNumber !== currentPageNumber);
        onUpdate(updatedData);
    };

    const pageExists = !!currentPageData;

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {hasChanges && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={onSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                    <Typography variant="caption" color="warning.main" sx={{ display: "block", textAlign: "center", mt: 1 }}>
                        You have unsaved changes
                    </Typography>
                </Paper>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">
                        Page {currentPageNumber} of {totalPages}
                    </Typography>
                    {!pageExists ? (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={handleAddPage}
                        >
                            Add Page
                        </Button>
                    ) : (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={handleRemovePage}
                            title="Remove page from identification"
                        >
                            <Delete />
                        </IconButton>
                    )}
                </Box>

                {pageExists ? (
                    <>
                        <Box sx={currentPageData.isStateTaxReturn ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                            <Autocomplete
                                options={formTypeOptions}
                                value={formTypeOptions.find((option) => option.value === formType) || null}
                                onChange={(_event, newValue, reason) => {
                                    handleFormTypeChange(newValue ? newValue.value as TaxReturnFormType : "");
                                    if (reason === 'clear') {
                                        setTimeout(() => formTypeRef.current?.blur(), 0);
                                    }
                                }}
                                getOptionLabel={(option) => option.label}
                                disabled={currentPageData.isStateTaxReturn}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Form Type"
                                        inputRef={formTypeRef}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Tab' && !e.shiftKey) {
                                                e.preventDefault();
                                                formPageRef.current?.focus();
                                            }
                                        }}
                                    />
                                )}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                type="text"
                                label="Form Page Number"
                                value={formPage === 0 ? "" : formPage}
                                onChange={(e) => handleFormPageChange(e.target.value)}
                                disabled={!formType || currentPageData.isStateTaxReturn}
                                sx={{ mb: 2 }}
                                inputRef={formPageRef}
                                onKeyDown={(e) => {
                                    if (e.key === 'Tab' && !e.shiftKey) {
                                        e.preventDefault();
                                        taxYearRef.current?.focus();
                                    } else if (e.key === 'Tab' && e.shiftKey) {
                                        e.preventDefault();
                                        formTypeRef.current?.focus();
                                    }
                                }}
                                slotProps={{
                                    htmlInput: {
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                type="text"
                                label="Tax Year"
                                value={taxYear === 0 ? "" : taxYear}
                                onChange={(e) => handleTaxYearChange(e.target.value)}
                                disabled={!formType || currentPageData.isStateTaxReturn}
                                sx={{ mb: 2 }}
                                inputRef={taxYearRef}
                                onKeyDown={(e) => {
                                    if (e.key === 'Tab' && e.shiftKey) {
                                        e.preventDefault();
                                        formPageRef.current?.focus();
                                    }
                                }}
                                slotProps={{
                                    htmlInput: {
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                    },
                                }}
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box>
                            {currentPageData.isStateTaxReturn && (
                                <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                                    State Tax Return (skipped)
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                Status: {currentPageData.isOnList ? "On List" : "Not on List"}
                            </Typography>
                            {!currentPageData.isOnList && currentPageData.identifiedForm && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                    Identified Form: {currentPageData.identifiedForm}
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                Confidence: {currentPageData.confidence.toFixed(1)}%
                            </Typography>
                            {currentPageData.reasoning && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    Reasoning: {currentPageData.reasoning}
                                </Typography>
                            )}
                        </Box>
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        This page is not in the identification list. Click &quot;Add Page&quot; to include it.
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default IdentifyPageSelector;
